import type { ReactNode } from 'react'

const cx = (...v: Array<string | false | undefined>) => v.filter(Boolean).join(' ')

export function Card({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cx('bg-surface border border-line rounded-[var(--radius-card)]', className)}>
      {children}
    </div>
  )
}

export function Button({
  children, onClick, variant = 'ghost', type = 'button', disabled,
}: {
  children: ReactNode
  onClick?: () => void
  variant?: 'primary' | 'ghost' | 'quiet'
  type?: 'button' | 'submit'
  disabled?: boolean
}) {
  const base =
    'inline-flex items-center gap-2 h-9 px-3.5 rounded-[var(--radius-control)] text-[14px] font-medium transition-colors disabled:opacity-50'
  const look = {
    primary: 'bg-accent text-white hover:bg-accent-ink',
    ghost: 'border border-line-strong text-ink hover:bg-accent-soft hover:border-accent',
    quiet: 'text-muted hover:text-ink hover:bg-page',
  }[variant]
  return (
    <button type={type} onClick={onClick} disabled={disabled} className={cx(base, look)}>
      {children}
    </button>
  )
}

export function Pill({
  children, active, onClick, count,
}: { children: ReactNode; active?: boolean; onClick?: () => void; count?: number }) {
  return (
    <button
      onClick={onClick}
      className={cx(
        'h-8 px-3 rounded-full text-[13.5px] font-medium transition-colors border',
        active
          ? 'bg-accent-soft border-accent text-accent-ink'
          : 'bg-surface border-line text-muted hover:text-ink hover:border-line-strong',
      )}
    >
      {children}
      {count !== undefined && <span className="ml-1.5 tnum text-[12px] opacity-70">{count}</span>}
    </button>
  )
}

export function Badge({ tone, children }: { tone: 'good' | 'warn' | 'bad' | 'neutral'; children: ReactNode }) {
  const look = {
    good: 'bg-good-soft text-good',
    warn: 'bg-warn-soft text-warn',
    bad: 'bg-bad-soft text-bad',
    neutral: 'bg-page text-muted',
  }[tone]
  return (
    <span className={cx('inline-flex items-center h-6 px-2.5 rounded-full text-[12.5px] font-medium', look)}>
      {children}
    </span>
  )
}

/** Suma se scrie la fel peste tot: moneda documentului mare, echivalentul in lei dedesubt,
 *  cifre monospatiate. Cursul folosit se vede la hover — nu e o cifra care apare din senin. */
export function Suma({
  valuta, moneda, inLei, explicatie, ton,
}: {
  valuta: number
  moneda: string
  inLei: number
  explicatie?: string
  ton?: 'muted' | 'strong'
}) {
  const f = (n: number) =>
    new Intl.NumberFormat('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n)
  const eLei = moneda === 'RON'
  return (
    <span className="inline-flex flex-col items-end leading-tight" title={explicatie}>
      <span className={cx('tnum whitespace-nowrap', ton === 'muted' && 'text-muted', ton === 'strong' && 'font-medium')}>
        {f(eLei ? inLei : valuta)}
        <span className="ml-1 text-[11.5px] text-muted">{eLei ? 'lei' : moneda}</span>
      </span>
      <span className="tnum text-[12px] text-muted whitespace-nowrap">
        {eLei ? f(valuta) : f(inLei)}
        <span className="ml-1 text-[11px]">{eLei ? 'EUR' : 'lei'}</span>
      </span>
    </span>
  )
}

export function EmptyState({ title, hint }: { title: string; hint?: string }) {
  return (
    <div className="py-20 text-center">
      <p className="text-[15px] font-medium">{title}</p>
      {hint && <p className="mt-1.5 text-[14px] text-muted">{hint}</p>}
    </div>
  )
}

export function Skeleton({ rows = 8 }: { rows?: number }) {
  return (
    <div className="p-4 space-y-3" aria-hidden>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="h-9 rounded-lg bg-page" />
      ))}
    </div>
  )
}
